import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";

const RestaurantContext = createContext(null);

// Special combined restaurant option
const COMBINED_RESTAURANT = {
  id: "combined",
  name: "Combined Order",
  isCombined: true
};

export const RestaurantProvider = ({ children }) => {
  const [restaurants, setRestaurants] = useState([]);
  const [currentRestaurant, setCurrentRestaurant] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadRestaurants = useCallback(async () => {
    try {
      const response = await api.get("/restaurants");
      const restaurantList = response.data;
      setRestaurants(restaurantList);
      
      // Auto-select restaurant if none selected
      const savedRestaurantId = localStorage.getItem("selectedRestaurantId");
      if (restaurantList.length > 0) {
        // Check if combined was selected
        if (savedRestaurantId === "combined") {
          setCurrentRestaurant(COMBINED_RESTAURANT);
        } else {
          const found = restaurantList.find(r => r.id === savedRestaurantId);
          if (found) {
            setCurrentRestaurant(found);
          } else if (!currentRestaurant) {
            // Select first restaurant if none saved
            selectRestaurant(restaurantList[0]);
          }
        }
      }
      return restaurantList;
    } catch (error) {
      console.error("Failed to load restaurants:", error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  const selectRestaurant = (restaurant) => {
    setCurrentRestaurant(restaurant);
    localStorage.setItem("selectedRestaurantId", restaurant.id);
  };
  
  // Get the combined restaurant option
  const getCombinedOption = () => COMBINED_RESTAURANT;

  const clearRestaurant = () => {
    setCurrentRestaurant(null);
    localStorage.removeItem("selectedRestaurantId");
  };

  // Initialize from localStorage on mount
  useEffect(() => {
    const savedRestaurantId = localStorage.getItem("selectedRestaurantId");
    if (savedRestaurantId) {
      // Set temporary current restaurant until full load
      setCurrentRestaurant({ id: savedRestaurantId, name: "Loading..." });
    }
  }, []);

  return (
    <RestaurantContext.Provider
      value={{
        restaurants,
        currentRestaurant,
        isLoading,
        loadRestaurants,
        selectRestaurant,
        clearRestaurant,
        getCombinedOption,
        isCombinedMode: currentRestaurant?.id === "combined",
      }}
    >
      {children}
    </RestaurantContext.Provider>
  );
};

export const useRestaurant = () => {
  const context = useContext(RestaurantContext);
  if (!context) {
    throw new Error("useRestaurant must be used within a RestaurantProvider");
  }
  return context;
};
